import { useCallback, useEffect, useRef, useState } from 'react';
import type { Graph } from '@shared/model/api';
import { Path, type Dataset } from '@shared/model/dataset';
import type { WorkerRequest, WorkerResponse } from '../worker/messages';

export type ItinerariesState =
    | { status: 'idle' }
    | { status: 'computing'; from: string; to: string; found: number }
    | {
          status: 'done';
          from: string;
          to: string;
          paths: Path[];
          durationMs: number;
      }
    | { status: 'error'; from: string; to: string; message: string };

/** Runs the itinerary search in a Web Worker; results are sorted by distance with titles computed. */
export function useItineraries(graph: Graph | undefined, dataset: Dataset | undefined) {
    const workerRef = useRef<Worker | null>(null);
    const requestId = useRef(0);
    const [state, setState] = useState<ItinerariesState>({ status: 'idle' });

    useEffect(() => {
        if (!graph || !dataset) return;
        const worker = new Worker(new URL('../worker/routing.worker.ts', import.meta.url), { type: 'module' });
        worker.postMessage({ type: 'init', graph } satisfies WorkerRequest);
        worker.onmessage = (event: MessageEvent<WorkerResponse>) => {
            const message = event.data;
            if (message.id !== requestId.current) return; // stale answer
            setState((current) => {
                if (current.status !== 'computing') return current;
                if (message.type === 'progress') return { ...current, found: message.found };
                if (message.type === 'error')
                    return {
                        status: 'error',
                        from: current.from,
                        to: current.to,
                        message: message.message,
                    };
                const paths = Path.sortByDistance(message.itineraries.map((codes) => dataset.pathFromCodes(codes)));
                Path.markDistinctive(paths);
                return {
                    status: 'done',
                    from: current.from,
                    to: current.to,
                    paths,
                    durationMs: message.durationMs,
                };
            });
        };
        workerRef.current = worker;
        return () => {
            worker.terminate();
            workerRef.current = null;
        };
    }, [graph, dataset]);

    const search = useCallback((from: string, to: string) => {
        const worker = workerRef.current;
        if (!worker) return;
        const id = ++requestId.current;
        setState({ status: 'computing', from, to, found: 0 });
        worker.postMessage({
            type: 'search',
            id,
            from,
            to,
        } satisfies WorkerRequest);
    }, []);

    const reset = useCallback(() => {
        requestId.current++;
        setState({ status: 'idle' });
    }, []);

    return { state, search, reset };
}
